import React, { FC, useMemo, useRef, ReactElement } from 'react';
import { css } from '@emotion/core';
import { SloDTO, SloQualityResultItemDTO } from '@atlassiansox/phobos-client-ts';
import Lozenge from '@atlaskit/lozenge';
import pluralize from 'pluralize';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tooltip from '@atlaskit/tooltip';

import { useLocationController } from '@/controllers';
import { Item, HeroNumber } from '@/components/primitive';
import { WARNING_COLOR, SPACE_COMPACT, SPACE_COZY, spaceTopCompact } from '@/styles';

import {
  getErrorBudget,
  formatPerformanceResult,
  formatObjectiveResult,
  formatErrorBudget,
  formatQualityScoreResult,
  getHeroNumberColorStyles,
} from '../common/utils';

import ErrorBudgetHelper from './summary-helpers/error-budget-helper';
import SLIHelper from './summary-helpers/sli-helper';
import QualityScoreHelper from './summary-helpers/quality-score/quality-score-helper';

const rootStyles = css`
  display: flex;
  gap: ${SPACE_COZY}px;
`;

const qualityScoreAppendixStyles = css`
  position: relative;
  top: -3px;
  margin-left: ${SPACE_COMPACT}px;
  cursor: default;
`;

const qualityScoreAppendixIconStyles = css`
  position: relative;
  top: 1px;
  & > div,
  & > div > span: first-of-type {
    display: inline;
  }
`;

type SloPageSummaryProps = Pick<SloDTO, 'performance' | 'objective' | 'quality'> & {
  isSkeleton?: boolean;
};

const SloPageSummary: FC<SloPageSummaryProps> = ({
  performance,
  objective,
  quality,
  isSkeleton,
}) => {
  const { isSLOGroupMemberPage } = useLocationController();

  const result = performance && performance.result;
  const sloQualityIssueCount: number = useMemo(() => {
    if (!quality || !quality.sloQualityResultItems) {
      return 0;
    }

    return quality.sloQualityResultItems.filter(
      (item: SloQualityResultItemDTO) => item.hasQualityIssue
    ).length;
  }, [quality]);

  const { sloQualityWarnings, sloQualityWarningCount } = useMemo(() => {
    let warnings: (string | ReactElement)[] = [];
    if (!quality || !quality.sloQualityResultItems) {
      warnings = [];
    } else {
      warnings = quality.sloQualityResultItems
        .filter((item: SloQualityResultItemDTO) => !!item.metaData?.warning)
        .flatMap((item: SloQualityResultItemDTO, index, array) =>
          index < array.length - 1
            ? [
                item.metaData?.warning as string,
                <span key={index}>
                  <br />
                  <br />
                </span>,
              ]
            : [item.metaData?.warning as string]
        );
    }

    return { sloQualityWarningCount: warnings.length, sloQualityWarnings: warnings };
  }, [quality]);

  const isSLOQualityAvailable = !!(
    quality &&
    quality.resultItemsCount &&
    quality.resultItemsCount > 0
  );

  const refSummary = useRef<HTMLDivElement>(null);

  return (
    <>
      <div css={[rootStyles]}>
        <Item title="Error Budget">
          <HeroNumber isSkeleton={isSkeleton}>
            {formatErrorBudget(getErrorBudget(result, objective))}
          </HeroNumber>
          <div css={spaceTopCompact}>
            <ErrorBudgetHelper objective={objective} performance={performance} />
          </div>
        </Item>
        <Item title="SLO - Target">
          <HeroNumber isSkeleton={isSkeleton}>{formatObjectiveResult(objective)}</HeroNumber>
        </Item>
        <Item
          title={`SLI - ${performance && performance.latestDataPointTimestamp ? '28 Days' : 'N/A'}`}
        >
          <HeroNumber
            isSkeleton={isSkeleton}
            css={getHeroNumberColorStyles({ performance, objective })}
          >
            {formatPerformanceResult(performance && performance.result)}
          </HeroNumber>
          <div css={spaceTopCompact}>
            <SLIHelper performance={performance} />
          </div>
        </Item>
        {!isSLOGroupMemberPage && (
          <Item title="Quality Score">
            <HeroNumber
              isFraction
              isSkeleton={isSkeleton}
              isUnitHidden={!isSLOQualityAvailable}
              css={getHeroNumberColorStyles({ quality, objective })}
              appendix={
                sloQualityIssueCount > 0 || sloQualityWarningCount > 0 ? (
                  <span css={qualityScoreAppendixStyles}>
                    {sloQualityIssueCount > 0 && (
                      <Lozenge appearance="moved">
                        {sloQualityIssueCount} {pluralize('issue', sloQualityIssueCount)}
                      </Lozenge>
                    )}
                    {sloQualityWarningCount > 0 && sloQualityIssueCount > 0 && (
                      <span css={qualityScoreAppendixIconStyles}>
                        <Tooltip content={sloQualityWarnings} position="right">
                          <WarningIcon primaryColor={WARNING_COLOR} label="warning-icon" />
                        </Tooltip>
                      </span>
                    )}
                    {sloQualityWarningCount > 0 && sloQualityIssueCount === 0 && (
                      <Tooltip content={sloQualityWarnings} position="right">
                        <Lozenge appearance="moved">
                          {sloQualityWarningCount} {pluralize('warning', sloQualityWarningCount)}
                        </Lozenge>
                      </Tooltip>
                    )}
                  </span>
                ) : null
              }
            >
              {formatQualityScoreResult(quality?.qualityScore)}
            </HeroNumber>
            {isSLOQualityAvailable && (
              <div css={spaceTopCompact}>
                <QualityScoreHelper quality={quality} refSummary={refSummary} />
              </div>
            )}
          </Item>
        )}
      </div>
      {/* Anchor element to set scroll target */}
      <span ref={refSummary} />
    </>
  );
};

export default SloPageSummary;
